import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  getAskHistoryDetail,
  getAskHistoryLogs,
  getAskHistorySummary,
  type AskHistoryDetail,
  type AskHistoryListItem,
} from "@/services/askHistory";
import MetricCardRow from "@/pages/admin/security-records/components/MetricCardRow";
import AdminLayout from "@/components/layout/AdminLayout";
import AdminPageTitle from "@/components/admin/AdminPageTitle";
import ConversationMessageList from "@/pages/opsmate/components/ConversationMessageList";
import { buildSessionLogItems } from "@/hooks/opsmate/sessionLogItems";
import AdminDataTable from "@/components/admin/AdminDataTable";
import AdminUserCell from "@/components/admin/AdminUserCell";
import { AdminSearchForm, AdminTableToolbar } from "@/components/admin/AdminTableToolbar";

function formatSeconds(value: unknown): string {
  const num = typeof value === "number" ? value : Number(value);
  return Number.isFinite(num) ? `${num.toFixed(1)}s` : "-";
}

// ─── Detail Page ──────────────────────────────────────────────────────────────
function ConversationHistoryDetailPage({ items, onBack }: { items: any[]; onBack: () => void }) {
  return (
    <div>
      <button
        onClick={onBack}
        className="flex items-center gap-1.5 text-sm text-gray-500 hover:text-gray-800 cursor-pointer mb-2 transition-colors group"
      >
        <span className="w-7 h-7 flex items-center justify-center rounded-lg bg-gray-100 group-hover:bg-gray-200 transition-colors">
          <i className="ri-arrow-left-s-line text-base"></i>
        </span>
        Back to list
      </button>

      <div className="mb-2">
        <h2 className="text-xl font-semibold text-gray-900">Log Detail</h2>
      </div>

      <div
        className="opsmate-page rounded-xl border border-gray-100 bg-white p-4"
        style={{ height: "calc(100vh - 210px)" }}
      >
        <ConversationMessageList items={items} hideTranslateAndFeedback />
      </div>
    </div>
  );
}

// ─── List Page ────────────────────────────────────────────────────────────────
interface ConversationHistoryTabProps {
  onDetailChange?: (isDetail: boolean) => void;
}

export default function ConversationHistoryTab({ onDetailChange }: ConversationHistoryTabProps) {
  const { t } = useTranslation();
  const [search, setSearch] = useState("");
  const [searchInput, setSearchInput] = useState("");
  const [selectedDetail, setSelectedDetail] = useState<AskHistoryDetail | null>(null);
  const [detailItems, setDetailItems] = useState<any[]>([]);
  const [logs, setLogs] = useState<AskHistoryListItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [detailLoadingId, setDetailLoadingId] = useState<string | null>(null);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);
  const [total, setTotal] = useState(0);
  const [summary, setSummary] = useState({
    totalRequests: 0,
    requestsPer5m: 0,
    successRate: 0,
    failureRate: 0,
    avgResponseMs: 0,
  });

  const handleBackToList = () => {
    setSelectedDetail(null);
    setDetailItems([]);
    onDetailChange?.(false);
  };

  const handleOpenDetail = async (log: AskHistoryListItem) => {
    setDetailLoadingId(log.id);
    try {
      const detail = await getAskHistoryDetail(log.id);
      const mapped = await buildSessionLogItems(Array.isArray(detail.logs) ? detail.logs : []);
      setDetailItems(mapped.items);
      setSelectedDetail(detail);
      onDetailChange?.(true);
    } finally {
      setDetailLoadingId(null);
    }
  };

  useEffect(() => {
    let cancelled = false;
    getAskHistorySummary()
      .then((data) => {
        if (!cancelled) setSummary(data);
      })
      .catch(() => {
        if (!cancelled) {
          setSummary({
            totalRequests: 0,
            requestsPer5m: 0,
            successRate: 0,
            failureRate: 0,
            avgResponseMs: 0,
          });
        }
      });
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    setPage(1);
  }, [search]);

  useEffect(() => {
    let cancelled = false;
    const handle = setTimeout(() => {
      setLoading(true);
      getAskHistoryLogs({ search, page, pageSize })
        .then((res) => {
          if (!cancelled) {
            setLogs(res.data);
            setTotal(res.total);
          }
        })
        .catch(() => {
          if (!cancelled) {
            setLogs([]);
            setTotal(0);
          }
        })
        .finally(() => {
          if (!cancelled) setLoading(false);
        });
    }, 300);
    return () => {
      cancelled = true;
      clearTimeout(handle);
    };
  }, [search, page, pageSize]);

  const trafficMetrics = useMemo(
    () => [
      {
        label: t("securityMonitor.traffic.totalRequests"),
        value: summary.totalRequests.toLocaleString(),
        status: "normal" as const,
      },
      {
        label: t("securityMonitor.traffic.requests5m"),
        value: summary.requestsPer5m,
        status: summary.requestsPer5m > 200 ? ("warning" as const) : ("normal" as const),
      },
      {
        label: t("securityMonitor.traffic.successRate"),
        value: `${summary.successRate}%`,
        status: Number(summary.successRate) < 95 ? ("warning" as const) : ("normal" as const),
      },
      {
        label: t("securityMonitor.traffic.failureRate"),
        value: `${summary.failureRate}%`,
        status: Number(summary.failureRate) > 2 ? ("critical" as const) : ("normal" as const),
      },
      {
        label: t("securityMonitor.traffic.avgResponseTime"),
        value: `${(summary.avgResponseMs / 1000).toFixed(1)}s`,
        unit: "",
        status: summary.avgResponseMs > 5000 ? ("warning" as const) : ("normal" as const),
      },
    ],
    [summary, t],
  );

  if (selectedDetail) {
    return (
      <AdminLayout>
        <ConversationHistoryDetailPage items={detailItems} onBack={handleBackToList} />
      </AdminLayout>
    );
  }

  const filtered = logs;
  function formatDateTime(iso: string) {
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return { date: "", time: "" };
    const pad = (n: number) => String(n).padStart(2, "0");
    const date = `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
    const time = `${pad(d.getHours())}:${pad(d.getMinutes())}`;
    return { date, time };
  }
  return (
    <AdminLayout>
      <div className="flex items-start justify-between mb-6">
        <div>
          <AdminPageTitle
            title="Ask History"
            tooltip="Browse complete conversation history, including full questions, AI answers, and step-by-step reasoning chains"
          />
        </div>
      </div>
      {/* QA Traffic Metrics */}
      <MetricCardRow metrics={trafficMetrics} />

      {/* Filters */}
      <AdminTableToolbar>
        <AdminSearchForm
          value={searchInput}
          onChange={setSearchInput}
          onSearch={() => setSearch(searchInput)}
          searchLabel={t("common.search")}
          placeholder="Search user, session ID or question..."
          showReset={search !== "" || searchInput !== ""}
          onReset={() => {
            setSearch("");
            setSearchInput("");
          }}
          resetLabel={t("common.reset")}
        />
      </AdminTableToolbar>

      <AdminDataTable
        headers={[
          t("systemLogs.col.time"),
          t("systemLogs.col.user"),
          t("systemLogs.col.sessionId"),
          t("systemLogs.col.question"),
          t("systemLogs.col.responseTime"),
          "Action",
        ]}
        columnWidths={["15%", "25%", "15%", "30%", "10%", "5%"]}
        colSpan={6}
        loading={loading}
        empty={filtered.length === 0}
        emptyText={t("systemLogs.noLogs")}
        emptyHint={t("users.table.noResultsHint")}
        total={total}
        page={page}
        pageSize={pageSize}
        onPageChange={(nextPage, nextPageSize) => {
          setPage(nextPage);
          setPageSize(nextPageSize);
        }}
      >
        {filtered.map((log) => {
          const { date, time } = formatDateTime(log.time);

          return (
            <tr key={log.id} className="border-b border-gray-50 hover:bg-gray-50 transition-colors">
              <td className="px-3 py-3 text-xs text-gray-500 whitespace-nowrap">
                <div>
                  <div className="text-sm font-semibold text-gray-900">{date}</div>
                  <div className="text-xs text-gray-400 mt-0.5">{time}</div>
                </div>
              </td>
              <td className="px-4 py-4 align-top">
                <AdminUserCell name={log.user} userId={log.userId} />
              </td>
              <td className="px-3 py-2.5 text-gray-500 whitespace-nowrap">{log.sessionId}</td>
              <td className="px-3 py-2.5 text-gray-600 max-w-xs truncate" title={log.question}>
                {log.question}
              </td>
              <td className="px-3 py-2.5 whitespace-nowrap">
                <span className="text-gray-500 font-medium">{formatSeconds(log.responseTime)}</span>
              </td>
              <td className="px-3 py-2.5">
                <button
                  onClick={() => handleOpenDetail(log)}
                  disabled={detailLoadingId === log.id}
                  className="w-7 h-7 flex items-center justify-center text-gray-500 hover:text-[#830051] cursor-pointer transition-colors"
                  title={t("systemLogs.viewDetail")}
                >
                  <i
                    className={`${detailLoadingId === log.id ? "ri-loader-4-line animate-spin" : "ri-eye-line"} text-sm`}
                  ></i>
                </button>
              </td>
            </tr>
          );
        })}
      </AdminDataTable>
    </AdminLayout>
  );
}
